import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Icono decorativo a la izquierda (búsqueda, teléfono…). */
  leading?: ReactNode;
  /** Contenido a la derecha: unidad, botón de limpiar, contador. */
  trailing?: ReactNode;
  /** Pinta el borde de error y marca `aria-invalid`. */
  invalid?: boolean;
}

/**
 * El contorno usa `border-border-input`, no `border-border`: WCAG 1.4.11 pide
 * 3:1 para los límites de un control, y el borde decorativo de tarjetas y
 * tablas no llega a ese contraste.
 */
export const inputBaseClass = cn(
  'flex w-full rounded-md border border-border-input bg-surface text-fg',
  'placeholder:text-fg-subtle',
  'transition-colors duration-fast ease-standard',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-1 focus-visible:ring-offset-canvas',
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-fg-disabled',
  'aria-[invalid=true]:border-danger-border aria-[invalid=true]:ring-danger/30',
);

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, leading, trailing, invalid, type = 'text', ...props },
  ref,
) {
  const field = (
    <input
      ref={ref}
      type={type}
      aria-invalid={invalid || undefined}
      className={cn(
        inputBaseClass,
        'h-control-md px-3 text-sm',
        leading ? 'pl-9' : undefined,
        trailing ? 'pr-9' : undefined,
        className,
      )}
      {...props}
    />
  );

  if (!leading && !trailing) return field;

  return (
    <div className="relative w-full">
      {leading ? (
        <span
          className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-fg-subtle [&_svg]:size-4"
          aria-hidden="true"
        >
          {leading}
        </span>
      ) : null}
      {field}
      {trailing ? (
        <span className="absolute right-2 top-1/2 flex -translate-y-1/2 text-fg-subtle [&_svg]:size-4">
          {trailing}
        </span>
      ) : null}
    </div>
  );
});
